/**
 * 3D Ring Viewer Custom Element
 * Uses WebGI runtime (same engine as the original 3rd-party viewer) for real-time
 * 3D ring rendering with Diamond plugin, engraving preview via SimpleTextPlugin,
 * and post-processing.
 * Loaded as ESM module via <script type="module"> in the Liquid template.
 */

import {
  ViewerApp,
  AssetManagerPlugin,
  addBasePlugins,
  CustomBumpMapPlugin,
  ShaderMaterial,
  Color,
  SimpleTextPlugin,
  setupCoreWebGiViewer,
} from 'https://dist.pixotronics.com/webgi/runtime/bundle-0.9.11.mjs?jenshansen';

// Bug fix #12 — WebGI's bundle auto-injects a loading-screen overlay that
// briefly shows "100%" every time the SimpleTextPlugin rebuilds its glyph
// atlas. The runtime uses several different class names across bundle
// versions, so we hide anything inside `<ring-viewer-3d>` that *looks* like
// a loading/progress overlay while preserving the theme's own spinner
// (`.ring-viewer-3d__loading`). The CSS layer is the catch-all; the
// MutationObserver below is the runtime fallback if the overlay lands in a
// shadow root or uses an exotic class.
(function injectWebGiLoadingScreenHide() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ring-viewer-3d-loading-suppress')) return;
  const style = document.createElement('style');
  style.id = 'ring-viewer-3d-loading-suppress';
  style.textContent = `
    ring-viewer-3d [class*="webgi-loading"],
    ring-viewer-3d [class*="webgl-loading"],
    ring-viewer-3d [class*="loading-screen"]:not(.ring-viewer-3d__loading),
    ring-viewer-3d [class*="LoadingScreen"],
    ring-viewer-3d [class*="loading-overlay"],
    ring-viewer-3d [class*="progress-bar"]:not(.ring-viewer-3d__loading),
    ring-viewer-3d [class*="progress"]:not(.ring-viewer-3d__loading),
    ring-viewer-3d [class*="threepipe-loader"],
    ring-viewer-3d [class*="pixotronics"] {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
})();

const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0212/3526/files/';

const MATERIAL_URLS = {
  'Yellow Gold': {
    body: CDN_BASE + 'PMAT-Yellow-Gold-Body.pmat?v=1676024488',
    hallmark: CDN_BASE + 'PMAT-Yellow-Gold-Hallmark.pmat?v=1676024489',
    insideEngrave: CDN_BASE + 'PMAT-Yellow-Gold-Inside-Engrave.pmat?v=1676024489',
    outsideEngraveLeft: CDN_BASE + 'PMAT-Yellow-Gold-Outside-Engrave-and-Left.pmat?v=1676023764',
    outsideEngraveRight: CDN_BASE + 'PMAT-Yellow-Gold-Outside-Engrave-Right.pmat?v=1676023764',
  },
  'White Gold/Platinum': {
    body: CDN_BASE + 'PMAT-White-Gold-Body.pmat?v=1675941665',
    hallmark: CDN_BASE + 'PMAT-White-Gold-Hallmark.pmat?v=1675941666',
    insideEngrave: CDN_BASE + 'PMAT-White-Gold-Inside-Engrave.pmat?v=1675941666',
    outsideEngraveLeft: CDN_BASE + 'PMAT-White-Gold-Outside-Engrave-and-Left.pmat?v=1676023764',
    outsideEngraveRight: CDN_BASE + 'PMAT-White-Gold-Outside-Engrave-Right.pmat?v=1676023764',
  },
  'Red Gold': {
    body: CDN_BASE + 'PMAT-Red-Gold-Body.pmat?v=1676023852',
    hallmark: CDN_BASE + 'PMAT-Red-Gold-Hallmark.pmat?v=1676023853',
    insideEngrave: CDN_BASE + 'PMAT-Red-Gold-Inside-Engrave.pmat?v=1676023853',
    outsideEngraveLeft: CDN_BASE + 'PMAT-Red-Gold-Outside-Engrave-and-Left.pmat?v=1676023764',
    outsideEngraveRight: CDN_BASE + 'PMAT-Red-Gold-Outside-Engrave-Right.pmat?v=1676023764',
  },
  'Sterling Silver': {
    body: CDN_BASE + 'PMAT-White-Gold-Body.pmat?v=1675941665',
    hallmark: CDN_BASE + 'PMAT-White-Gold-Hallmark.pmat?v=1675941666',
    insideEngrave: CDN_BASE + 'PMAT-White-Gold-Inside-Engrave.pmat?v=1675941666',
    outsideEngraveLeft: CDN_BASE + 'PMAT-White-Gold-Outside-Engrave-and-Left.pmat?v=1676023764',
    outsideEngraveRight: CDN_BASE + 'PMAT-White-Gold-Outside-Engrave-Right.pmat?v=1676023764',
  },
};

const VJSON_URL = CDN_BASE + 'Plugin_and_Camera_Settings_2023-04-05.vjson?v=1680691442';
const HDR_URL = CDN_BASE + 'Overhead_Array_4ka_Blurred__Higher__Brighter__640.hdr?v=1677842901';
// Canonical engraving font stylesheet — same extension the website's Elvish
// translator uses. The previous URL (fc0a64a3.../jens-hansen-nz-production-19)
// pointed at the older NZ store extension and was serving stale font files,
// which made the 3D viewer's engraving glyphs disagree with the website
// translator. Swapping in the US production extension restores parity.
// Engraving font stylesheet for SimpleTextPlugin's glyph rendering. The
// snippet passes the theme's own copy via data-engraving-css-url; for markup
// without the attribute, fall back to that same theme asset, resolved next to
// this script on the theme CDN. Nothing references the third-party app any more.
const ENGRAVING_CSS_FALLBACK_URL = (() => {
  try {
    const src = document.currentScript && document.currentScript.src;
    if (src) return new URL('engraving-fonts.css', src).href;
  } catch (e) { /* fall through */ }
  return '';
})();

const METAL_PRESETS_FALLBACK = {
  'Yellow Gold': { color: '#D4A843', metalness: 1.0, roughness: 0.18, envMapIntensity: 1.5 },
  'Red Gold': { color: '#B87650', metalness: 1.0, roughness: 0.20, envMapIntensity: 1.5 },
  'White Gold/Platinum': { color: '#D8D8D8', metalness: 1.0, roughness: 0.15, envMapIntensity: 1.5 },
  'Sterling Silver': { color: '#C8C8C8', metalness: 1.0, roughness: 0.18, envMapIntensity: 1.5 },
};

const MESH_NAMES = {
  ringBody: 'Ring_Body',
  diamond: 'Diamond',
  insideEngrave: 'Inside_Engrave',
  // Diamond rings split the outside engraving area into two faces around
  // the centre stone (Left/Right). Plain band GLBs don't have a stone in
  // the way, so they use a single Outside_Engrave_Full mesh that wraps
  // the whole outside surface. Discover both shapes so the same code path
  // works for the diamond AND plain-band models.
  outsideEngraveLeft: 'Outside_Engrave_Left',
  outsideEngraveRight: 'Outside_Engrave_Right',
  outsideEngraveFull: 'Outside_Engrave_Full',
  hallmark: 'Inside_Hallmark_1024',
};

// RSFY-675 — per-font calibration factors. Mirrors FONT_CONFIGS in
// engraving-fit-engine.js (Halfdan's Canvas-measurement-derived values so
// the 3D preview matches what gets physically engraved). The 0.4214 factor
// for Tengwar Annatar is the well-known 2048/4860 EM-square correction.
// Keyed by both display name and CSS family so callers can pass either.
const FONT_SCALE_BY_NAME = {
  'Elvish': 0.4214,
  'Tengwar': 0.4214,
  'Tengwar Annatar': 0.4214,
  'Tengwar Elvish': 0.4214,
  'Kereru': 0.7116,
  'Kereru Italic': 0.7116,
  'Bookman Old Style': 0.8889,
  'Colonna MT': 0.8889,
  'Edwardian Script': 0.811,
  'Lucida Handwriting': 0.7787,
  'Lucida Calligraphy': 0.7348,
  'Apple Chancery': 0.6242,
  'Old English': 0.8475,
  'Script Bold': 0.8305,
  'Typewriter': 0.9497,
};

function scaleForFont(font) {
  if (!font) return 1.0;
  return FONT_SCALE_BY_NAME[font] != null ? FONT_SCALE_BY_NAME[font] : 1.0;
}

if (!customElements.get('ring-viewer-3d')) {
  customElements.define(
    'ring-viewer-3d',
    class RingViewer3D extends HTMLElement {
      constructor() {
        super();
        this.viewer = null;
        this.meshes = {};
        // Resident model sets keyed by GLB URL. On the PDP both variant models
        // stay loaded (like the original viewer's _A/_B mesh sets) and a
        // stone-quality change is a visibility flip — no reload, no scene
        // update, no FrameFade. The ring builder cycles through many GLBs and
        // keeps replacing them instead.
        this._modelSets = {};
        this._loadingSets = {};
        this._registeredMeshes = new WeakSet();
        this.textPlugin = null;
        this._fontStyles = '';
        this.currentModelUrl = null;
        this.initialized = false;
        this.variantUnsubscriber = null;
        // Pending engraving updates while a model is still loading. When the
        // ring builder switches rings before the new GLB's meshes have
        // hydrated, `updateEngraving` would otherwise silently no-op. We
        // stash the last write per side here and re-apply it the moment
        // loadModel completes.
        this._pendingEngraving = { outside: null, inside: null };
      }

      connectedCallback() {
        if (this.hasAttribute('data-manual-init')) return;
        this.initWhenVisible();
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        if (this.observer) this.observer.disconnect();
        if (this._visibilityFallback) { clearInterval(this._visibilityFallback); this._visibilityFallback = null; }
        if (this._onScrollFallback) window.removeEventListener('scroll', this._onScrollFallback);
        if (this._loadingObserver) this._loadingObserver.disconnect();
        if (this.viewer) {
          this.viewer.canvas?.remove();
          this.viewer.dispose();
        }
        document.removeEventListener('ring-engraving-update', this.boundEngravingHandler);
      }

      initWhenVisible() {
        const start = () => {
          if (this.initialized) return;
          this.initialized = true;
          if (this.observer) this.observer.disconnect();
          if (this._visibilityFallback) { clearInterval(this._visibilityFallback); this._visibilityFallback = null; }
          window.removeEventListener('scroll', this._onScrollFallback);
          this.init();
        };
        this.observer = new IntersectionObserver(
          (entries) => { if (entries[0].isIntersecting) start(); },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
        // Belt and braces: IntersectionObserver callbacks ride the rendering
        // pipeline, and on busy pages / slow machines they can lag by seconds
        // or not arrive while the tab is throttled (31 of 208 PDPs needed a
        // manual kick in a scripted browser run, 2026-09-02). Poll the
        // geometry as well and start as soon as the viewer is near the viewport.
        const nearViewport = () => {
          const r = this.getBoundingClientRect();
          return r.width > 0 && r.bottom > -200 && r.top < (window.innerHeight || 0) + 200;
        };
        this._onScrollFallback = () => { if (nearViewport()) start(); };
        window.addEventListener('scroll', this._onScrollFallback, { passive: true });
        this._visibilityFallback = setInterval(() => { if (nearViewport()) start(); }, 750);
      }

      async init() {
        // Idempotent: the IntersectionObserver, initManual() and any external
        // caller may all race to start us (a second init disposes the first
        // load's meshes and leaves the ring un-materialised — seen under
        // Playwright when a scripted init() met the observer's own).
        if (this._initStarted) return;
        this._initStarted = true;
        try {
          await this.initViewer();
          // Start with whichever variant the page opened on: the picker keeps
          // `?variant=` across reloads, so a customer who chose the second
          // stone size must see that model, not model A (the original viewer
          // checks the checked radio on load for the same reason).
          const startWithB = this._selectedOptionsMatchB();
          await this.loadModel(startWithB ? this.dataset.variantBModel : this.dataset.variantAModel);
          await this._prepareModelSet(this._modelSets[this.currentModelUrl]);
          this.showDisclaimer();
          if (startWithB) this.updateDisclaimer(this.dataset.variantBDisclaimer);
          this.bindEvents();
          // RSFY-805 motion plan 001: no minimum display time for the loading
          // screen. It hides the moment the model, metal and offsets are applied.
          this._preloadOtherVariant();
          this.hideLoading();
        } catch (error) {
          console.error('[3D Ring Viewer] Initialization error:', error);
          this.showError();
        }
      }

      async initViewer() {
        const canvas = this.querySelector('.ring-viewer-3d__canvas');
        const bgColor = this.dataset.bgColor || '#E6E8EB';

        // Bug fix #12 — pass every known opt-out flag for the WebGI loading
        // screen so the bundle never installs the built-in overlay in the
        // first place. Unknown flags are ignored by the runtime.
        const viewer = await setupCoreWebGiViewer({ canvas }, {
          disableLoadingScreen: true,
          loadingScreen: false,
          showLoadingScreen: false,
        });
        this.viewer = viewer;

        // Belt-and-braces: try every plugin-name variant the runtime has used
        // across versions. Guarded so a missing API doesn't throw.
        // RSFY-680 — also strip the InteractionPromptPlugin, which paints a
        // pointer/finger icon on the model the first few seconds. Halfdan
        // flagged this as a distraction (showed up as a "gray artifact" on
        // top of the ring in his demo).
        try {
          const remove = viewer.removePluginByType || viewer.removePlugin;
          if (typeof remove === 'function') {
            [
              'LoadingScreenPlugin', 'LoadingScreen', 'ProgressPlugin', 'AssetManagerLoadingScreenPlugin',
              'InteractionPromptPlugin', 'InteractionPrompt',
            ].forEach((name) => { try { remove.call(viewer, name); } catch (_) {} });
          }
        } catch (e) {
          // Non-fatal — the CSS + observer layers still suppress the overlay.
        }

        // Third line of defence: a MutationObserver on this custom element.
        // Any node added later that smells like a loading/progress overlay
        // gets hidden immediately. Catches shadow-DOM-less overlays the
        // bundle attaches after viewer setup (e.g. on glyph-atlas rebuilds).
        this._installLoadingOverlaySuppressor();

        const manager = viewer.getPlugin(AssetManagerPlugin);
        this.manager = manager;

        // Load vjson config (plugins + camera). The asset occasionally fails
        // to import (newer WebGI runtimes don't recognise the older format,
        // some CDNs serve it with application/octet-stream which the
        // importer rejects). When that happens we still want the viewer to
        // come up — HDR + materials + post-FX can be configured imperatively
        // below, so a missing camera/plugin preset isn't fatal.
        try {
          await manager.addFromPath(VJSON_URL);
        } catch (e) {
          console.warn('[3D Ring Viewer] vjson preset unavailable, continuing without it:', e?.message || e);
        }

        // Set HDR environment
        try {
          await viewer.scene.setEnvironment(
            await manager.importer.importSinglePath(HDR_URL)
          );
        } catch (e) {
          console.warn('[3D Ring Viewer] HDR environment load failed:', e?.message || e);
        }

        // Background color
        if (viewer.scene) {
          viewer.scene.backgroundColor = new Color(bgColor);
          viewer.scene.background = null;
        }

        // Override rendering plugin values with theme editor settings
        const bloomVal = parseFloat(this.dataset.bloomIntensity || 5) / 10;
        const ssrVal = parseFloat(this.dataset.ssrIntensity || 10) / 10;
        const envVal = parseFloat(this.dataset.envIntensity || 15) / 10;
        this._envIntensity = envVal;

        const bloomPlugin = viewer.getPlugin('Bloom') || viewer.getPlugin('BloomPlugin');
        if (bloomPlugin?.pass) bloomPlugin.pass.intensity = bloomVal;

        const ssrPlugin = viewer.getPlugin('SSReflection') || viewer.getPlugin('SSReflectionPlugin');
        if (ssrPlugin?.pass) ssrPlugin.pass.intensity = ssrVal;

        if (viewer.scene) viewer.scene.environmentIntensity = envVal;

        // Slow down orbit controls — small jewellery objects feel too fast at default speed
        const controls = viewer.scene.activeCamera?._controls;
        if (controls) {
          controls.rotateSpeed = 0.3;
          controls.zoomSpeed = 0.5;
          controls.dampingFactor = 0.04;
        }

        // SimpleTextPlugin — same approach as the original 3rd-party viewer
        this.textPlugin = await viewer.addPlugin(SimpleTextPlugin);
        this.textPlugin.applyToMap = true;
        this.textPlugin.applyToBumpMap = true;
        this.textPlugin.applyToAlphaMap = false;

        // updateText is async: it awaits importSinglePath to rasterize the SVG
        // and upload the engraving texture to the GPU. The renderer is
        // on-demand (setDirty), so once that promise resolves the new texture
        // sits on the material but never paints until some later interaction —
        // which is why switching the engraving language/font (Elvish→English)
        // left the freshly-rendered text invisible. Wrap updateText so EVERY
        // call — on every code path/branch — re-marks the viewer dirty the
        // instant its texture upload finishes, guaranteeing the engraving
        // paints with no further interaction.
        if (!this.textPlugin.__updateTextRenderWrapped) {
          const origUpdateText = this.textPlugin.updateText.bind(this.textPlugin);
          const self = this;
          this.textPlugin.updateText = function (...args) {
            const result = origUpdateText(...args);
            const flush = () => self.viewer && self.viewer.setDirty();
            Promise.resolve(result).then(flush).catch(() => {});
            // Extra ticks cover the texture's own GPU upload landing a frame
            // or two after the promise resolves.
            requestAnimationFrame(() => requestAnimationFrame(flush));
            return result;
          };
          this.textPlugin.__updateTextRenderWrapped = true;
        }

        // Fetch engraving font CSS (contains @font-face for Elvish, Kereru, etc.).
        // RSFY-709 #4 — retry once on network blip; without this a transient
        // CDN failure during preview-mode page load leaves `_fontStyles`
        // empty, which silently strips the @font-face declarations from the
        // SVG textures and the engraving falls back to the browser default
        // (~2× larger glyphs vs. the live theme that fetched the CSS cleanly).
        const fetchFontCss = async (attempt = 0) => {
          try {
            const res = await fetch(this.dataset.engravingCssUrl || ENGRAVING_CSS_FALLBACK_URL, { cache: 'force-cache' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // SimpleTextPlugin.embedUrlRefs() inlines only `(ftp|https?)://`
            // refs into the texture SVG; Shopify's asset_url emits
            // protocol-relative `//host/…` src URLs, which that regex skips —
            // the @font-face survives as an external ref, SVG-as-image
            // rasterization blocks it, and the engraving silently renders in
            // a Latin fallback font. Force an explicit scheme.
            return (await res.text()).replace(/url\(\s*(['"]?)\/\//gi, 'url($1https://');
          } catch (e) {
            if (attempt < 1) {
              await new Promise((r) => setTimeout(r, 400));
              return fetchFontCss(attempt + 1);
            }
            console.warn('[3D Ring Viewer] Failed to load engraving font CSS:', e.message);
            return null;
          }
        };
        this._fontStyles = (await fetchFontCss()) || '';
      }

      async loadModel(url) {
        if (!url || !this.viewer) return;
        if (this.currentModelUrl === url) return;

        // Already resident (the PDP's other variant model): swap by
        // visibility. No scene update, so no FrameFade and no reload — the
        // switch is instant, exactly like the original viewer's mesh toggle.
        if (this._modelSets[url]) {
          this.currentModelUrl = url;
          this._activateModelSet(url);
          return;
        }

        // Keep the other models resident only while every loaded model is one
        // of the PDP's two variant models. The ring builder cycles through
        // many GLBs and must keep replacing them: AssetManagerPlugin.addFromPath
        // only *appends* to the scene, and stacked models render the plain
        // band's setting-free Ring_Body on top of the diamond model's
        // prong-bearing one (ghost prongs). disposeSceneModels removes every
        // loaded scene asset in one shot.
        const resident = new Set([this.dataset.variantAModel, this.dataset.variantBModel].filter(Boolean));
        const keepOthers = resident.has(url) && Object.keys(this._modelSets).every((u) => resident.has(u));
        if (!keepOthers && this.currentModelUrl && this.viewer.scene?.disposeSceneModels) {
          try { this.viewer.scene.disposeSceneModels(); } catch (e) {}
          this._modelSets = {};
          this._registeredMeshes = new WeakSet();
        }
        this.meshes = {};
        this.currentModelUrl = url;

        await this._ensureModelSet(url);
        // The customer may have switched again while this GLB was in flight —
        // show whichever model is the latest intent.
        this._activateModelSet(this.currentModelUrl, { replayEngraving: true });
      }

      /**
       * Load a GLB into the scene (once — concurrent callers share the same
       * promise) and register it as a model set: its named meshes plus the
       * top-level roots they hang from, so the whole model can be shown or
       * hidden as a unit. Other resident sets are left untouched.
       */
      _ensureModelSet(url) {
        if (this._modelSets[url]) return Promise.resolve(this._modelSets[url]);
        if (this._loadingSets[url]) return this._loadingSets[url];
        const p = (async () => {
          const modelRoot = this.viewer.scene.modelRoot;
          await this.manager.addFromPath(url);

          const meshes = {};
          const roots = new Set();
          this.viewer.scene.traverse((child) => {
            if (!(child.isMesh || child.material) || this._registeredMeshes.has(child)) return;
            for (const [key, name] of Object.entries(MESH_NAMES)) {
              if (child.name === name) {
                meshes[key] = child;
                this._registeredMeshes.add(child);
                let top = child;
                while (top.parent && top.parent !== modelRoot && top.parent.type !== 'Scene') top = top.parent;
                roots.add(top);
              }
            }
          });

          // Hallmark AO requires uv2 attribute
          if (meshes.hallmark && meshes.hallmark.geometry) {
            const geo = meshes.hallmark.geometry;
            if (!geo.attributes.uv2 && geo.attributes.uv) {
              geo.setAttribute('uv2', geo.attributes.uv);
            }
          }

          // Client revision (2026-08-11): with outside engraving, the text on a
          // PLAIN BAND lands on the face pointing away from the default camera,
          // so the customer sees a blank ring until they drag it (the client's
          // screenshot: band + Tengwar, engraving on the back).
          //
          // The fix is per-model, NOT blanket: engraving position is baked into
          // each GLB's UV map, and the two batches are authored differently —
          // the customer-supplied band models (W_*.glb) put the text region at
          // the back, while the diamond setting models (E_*.glb) put it at the
          // front (verified live: sleek + English renders facing the camera).
          // A blanket 180° would fix the bands and break every diamond setting.
          //
          // Applied to the model rather than the camera: the camera pose comes
          // from the vjson preset, and moving it would also move the tuned
          // lighting reflections. UV-mapped textures are unaffected by object
          // rotation. Applied to this set's roots only, so a resident band
          // model is never rotated twice.
          try {
            const file = String(url).split('/').pop() || '';
            if (/^W_/i.test(file)) {
              roots.forEach((r) => { r.rotation.y += Math.PI; r.updateMatrixWorld && r.updateMatrixWorld(true); });
            }
          } catch (e) { /* orientation is cosmetic — never block the load */ }

          const set = { url, meshes, roots, prepared: false };
          this._modelSets[url] = set;
          return set;
        })();
        this._loadingSets[url] = p;
        p.finally(() => { delete this._loadingSets[url]; });
        return p;
      }

      _setModelSetVisible(set, visible) {
        set.roots.forEach((r) => {
          r.visible = visible;
          // The ground plugin bakes shadows from every mesh in the scene,
          // visible or not, so a hidden resident model would leave its own
          // shadow under the ring on screen. Shadow casting follows visibility.
          r.traverse((o) => { if (o.isMesh) o.castShadow = visible; });
        });
        Object.values(set.meshes).forEach((m) => { if (m) m.visible = visible; });
        this._refreshGround();
      }

      /**
       * The ground plugin parks its plane at the bottom of the VISIBLE scene
       * bounds, but only re-measures on a scene update — which a visibility
       * flip between resident sets deliberately never raises. The two stone
       * models differ in height (the .50ct head reaches 0.048 lower than the
       * .33ct one), so after a flip the ground stayed where the first model
       * put it: land on .33ct and switch to .50ct and the ring sank into the
       * plane with the stone's tip cut off (RSFY-800 bug 1, reported again
       * on the dev pass 2026-09-08); the other way round the ring floated.
       * Ask for the re-measure on every flip, then rebake the shadows.
       */
      _refreshGround() {
        try {
          const g = this.viewer && this.viewer.getPlugin && (this.viewer.getPlugin('Ground') || this.viewer.getPlugin('GroundPlugin'));
          if (!g) return;
          if (typeof g.refreshTransform === 'function') g.refreshTransform();
          if (typeof g.bakeShadows === 'function') g.bakeShadows();
        } catch (e) { /* cosmetic — never block a switch */ }
      }

      /**
       * Show one resident set, hide the rest, and point `this.meshes` at it.
       * Replays the latest engraving writes because the text may have changed
       * while another set was on screen. Renderer dirty only — a scene update
       * would trigger WebGi's FrameFade (the original viewer avoids it the
       * same way).
       */
      _activateModelSet(url, { replayEngraving = false } = {}) {
        const set = this._modelSets[url];
        if (!set) return;
        for (const [u, s] of Object.entries(this._modelSets)) this._setModelSetVisible(s, u === url);
        this.meshes = set.meshes;
        if (set.prepared) this._bakeDiamondIfVisible(set);
        // A freshly loaded model replays any engraving writes that arrived
        // while it was in flight — the ring-builder case where text/font is
        // pushed straight after asking for a different GLB, before
        // `this.meshes` has hydrated. A flip between resident sets skips it:
        // they share materials, so the texture is already there (multi-frame
        // schedule because a fresh model needs a frame or two to wire its
        // materials to the renderer).
        if (replayEngraving) this._scheduleFlushPendingEngraving();
        this.viewer.setDirty();
      }

      /**
       * Materials for a set, once. The first set imports its metal materials;
       * every later resident set SHARES those material objects, exactly as the
       * original viewer assigns one material per part to both its _A and _B
       * meshes. SimpleTextPlugin writes the engraving texture onto the
       * material, so the hidden model is always engraved and up to date and a
       * stone-quality switch needs no texture work at all — that rebuild was
       * the hitch that remained after the visibility flip.
       */
      async _prepareModelSet(set) {
        if (!set || set.prepared) return;
        set.prepared = true;
        const primary = Object.values(this._modelSets).find((s) => s !== set && s.materialsReady);
        if (primary) {
          const alias = { outsideEngraveFull: 'outsideEngraveLeft', outsideEngraveLeft: 'outsideEngraveFull' };
          for (const key of ['ringBody', 'hallmark', 'insideEngrave', 'outsideEngraveLeft', 'outsideEngraveRight', 'outsideEngraveFull']) {
            const mesh = set.meshes[key];
            const src = primary.meshes[key] || (alias[key] && primary.meshes[alias[key]]);
            if (!mesh || !src || !src.material) continue;
            if (mesh.setMaterial) mesh.setMaterial(src.material); else mesh.material = src.material;
          }
          this._bakeDiamondIfVisible(set);
          set.materialsReady = true;
          this.viewer.setDirty();
          return;
        }
        this._bakeDiamondIfVisible(set);
        await this.applyMetal(this.dataset.metalType, set.meshes);
        this.applyPolygonOffset(set.meshes);
        set.materialsReady = true;
      }

      /**
       * The Diamond plugin bakes a normal cube map (tCubeMapNormals) by
       * rendering the stone's mesh; an invisible mesh renders nothing, so a
       * diamond baked while its set is hidden has no refraction map and
       * renders flattened, sunk into the setting — RSFY-800 bug 1, which the
       * original viewer has for the same reason (it hides model B before its
       * bake completes). Bake only while the set is on screen: at init for
       * the first model, on first activation for the resident second one.
       */
      _bakeDiamondIfVisible(set) {
        if (!set || set.diamondReady) return;
        const root = set.roots.values().next().value;
        if (root && root.visible === false) return;
        this.applyDiamondMaterial(set.meshes);
        set.diamondReady = true;
      }

      /**
       * The original viewer loads BOTH variant GLBs at init and toggles mesh
       * visibility, so a stone-quality switch is instant. Load the second
       * model resident but hidden once the first is on screen, materials
       * prepared, so the first switch is a visibility flip too (prod-vs-dev
       * diff, 2026-09-02: dev faded and lagged where the live site snapped).
       */
      _selectedOptionsMatchB() {
        const termB = this.dataset.variantBTerm;
        if (!termB || !this.dataset.variantBModel) return false;
        const scope = this.closest('.shopify-section') || document;
        const values = [];
        scope.querySelectorAll('input[type="radio"][name^="options["]:checked').forEach((i) => values.push(i.value));
        scope.querySelectorAll('select[name^="options["]').forEach((el) => values.push(el.value));
        return values.some((v) => String(v).toLowerCase().includes(termB.toLowerCase()));
      }

      async _preloadOtherVariant() {
        const a = this.dataset.variantAModel, bModel = this.dataset.variantBModel;
        if (!bModel || bModel === a) return;
        const b = this.currentModelUrl === bModel ? a : bModel; // the one NOT on screen
        if (!b || this._modelSets[b] || this._preloadingB) return;
        this._preloadingB = true;
        // Let the first paint settle before competing for bandwidth and CPU.
        await new Promise((r) => setTimeout(r, 1500));
        try {
          if (this.viewer && !this._modelSets[b]) {
            await this._withoutSceneAnimations(async () => {
              const set = await this._ensureModelSet(b);
              this._setModelSetVisible(set, this.currentModelUrl === b);
              await this._prepareModelSet(set);
              if (this.currentModelUrl === b) this._activateModelSet(b);
            });
          }
        } catch (e) {
          console.warn('[3D Ring Viewer] variant B preload failed, will load on demand:', e?.message || e);
        }
        this._preloadingB = false;
      }

      // Adding a model to a live scene is a scene update. Two preset plugins
      // react to that visibly: FrameFadePlugin cross-fades the frame, and
      // InteractionPromptPlugin swings the camera to invite dragging. The
      // swing is fine on a real stone switch (the live site does the same),
      // but during a background preload the GLB parse and material import
      // starve the animation loop, the camera froze mid-swing for seconds and
      // the ring sat rotated with its engraving pushed round the band
      // (dev pass, 2026-09-04). Wait for the camera to be still, then hold
      // both plugins off for the duration of the load only.
      async _withoutSceneAnimations(fn) {
        const get = (n) => (this.viewer.getPlugin && (this.viewer.getPlugin(n) || this.viewer.getPlugin(n + 'Plugin'))) || null;
        const ff = get('FrameFade'), ip = get('InteractionPrompt');
        await this._waitForStillCamera(6000);
        const wasFf = ff ? ff.enabled : undefined, wasIp = ip ? ip.enabled : undefined;
        const lastAction = ip ? ip.lastActionTime : undefined;
        const wasRunning = !!(ip && ip.animationRunning);
        const phase = ip ? ip.currentTime : 0;
        let touched = false;
        const onTouch = () => { touched = true; };
        const container = this.viewer.container;
        if (container) { container.addEventListener('pointerdown', onTouch, true); container.addEventListener('wheel', onTouch, true); }
        if (ff) ff.enabled = false;
        if (ip) {
          // A swing that is still running is stopped and the camera put back
          // on its rest pose; stopAnimation() alone leaves it wherever the
          // last frame wrote it.
          if (ip.animationRunning && ip.currentSphericalPosition) {
            const cam = this.viewer.scene.activeCamera;
            if (typeof ip.stopAnimation === 'function') ip.stopAnimation();
            cam.position.setFromSpherical(ip.currentSphericalPosition).add(cam.target);
            if (typeof cam.positionUpdated === 'function') cam.positionUpdated();
          }
          ip.enabled = false;
        }
        try { return await fn(); } finally {
          if (container) { container.removeEventListener('pointerdown', onTouch, true); container.removeEventListener('wheel', onTouch, true); }
          if (ff && wasFf !== undefined) ff.enabled = wasFf;
          if (ip && wasIp !== undefined) {
            ip.enabled = wasIp;
            if (touched) {
              // The user took over while the plugin was off; that is what
              // its own pointerdown handler would have recorded.
              ip.lastActionTime = performance.now();
            } else if (wasRunning && wasIp && typeof ip.startAnimation === 'function') {
              // Resume the prompt loop in its pause phase so the cadence
              // continues as on the live site instead of restarting 30s later.
              ip.startAnimation();
              ip.currentTime = Math.max(phase, (ip.animationDuration || 2000) + 60);
            } else if (lastAction !== undefined) {
              // The plugin re-arms its swing timer on every addSceneObject;
              // the background preload is not a user-visible event.
              ip.lastActionTime = lastAction;
            }
          }
        }
      }

      _waitForStillCamera(maxMs) {
        return new Promise((resolve) => {
          const cam = this.viewer && this.viewer.scene && this.viewer.scene.activeCamera;
          if (!cam || !cam.position) return resolve();
          const t0 = Date.now(); let last = cam.position.clone(); let still = 0;
          const tick = () => {
            const now = cam.position;
            if (now.distanceTo(last) < 1e-4) still += 1; else still = 0;
            last = now.clone();
            if (still >= 4 || Date.now() - t0 > maxMs) return resolve();
            setTimeout(tick, 250);
          };
          tick();
        });
      }

      /**
       * Re-apply any engraving writes that arrived before the model was
       * ready. No-op when nothing is pending or the text plugin hasn't
       * spun up yet.
       */
      _flushPendingEngraving() {
        if (!this.textPlugin || !this._pendingEngraving) return;
        const pending = this._pendingEngraving;
        if (pending.outside) {
          const { text, font } = pending.outside;
          this.updateEngraving('outside', text, font);
        }
        if (pending.inside) {
          const { text, font } = pending.inside;
          this.updateEngraving('inside', text, font);
        }
      }

      /**
       * Schedule the flush across multiple animation frames + delays so it
       * survives the asynchronous bits of SimpleTextPlugin: web-font load
       * (Elvish + custom fonts cache after the first call), texture upload
       * to the GPU, and the renderer's own dirty-tracking. A bare flush
       * called synchronously right after applyMetal sometimes lands while
       * the new material's bumpMap/map slots haven't been wired to a frame
       * yet, leaving the engraving invisible until the customer types
       * another keystroke. Retrying at 0/250/700ms covers every case we've
       * seen without spamming the GPU.
       */
      _scheduleFlushPendingEngraving() {
        if (this._flushRetryTimers) {
          this._flushRetryTimers.forEach((t) => clearTimeout(t));
        }
        this._flushRetryTimers = [];
        const run = () => {
          this._flushPendingEngraving();
          this.viewer?.setDirty();
        };
        // Synchronous attempt — works when nothing else is pending.
        run();
        // Next-frame retry — covers the SimpleTextPlugin font/texture
        // upload that lands on the following render frame.
        requestAnimationFrame(() => requestAnimationFrame(run));
        // Belt-and-braces retries for slow networks / cold font cache.
        this._flushRetryTimers.push(setTimeout(run, 250));
        this._flushRetryTimers.push(setTimeout(run, 700));
      }

      /**
       * Mark the viewer dirty across the next few frames so a freshly-written
       * engraving texture actually paints. SimpleTextPlugin.updateText is
       * async (it awaits importSinglePath to build + upload the SVG texture);
       * by the time that resolves the renderer may already be idle, so the new
       * map/bumpMap sits on the material but never reaches the screen until the
       * next unrelated redraw (a drag, a hover, another keystroke).
       *
       * This bit users when switching the engraving FONT: typing fires frequent
       * redraws that happened to flush the texture, but a lone language/font
       * switch (e.g. Elvish → English) produced no follow-up redraw, so the
       * English glyphs — though correctly rasterized into the texture — stayed
       * invisible until the next interaction. Nudging setDirty over 0/2-frame/
       * 120ms/360ms covers the async upload without spamming the GPU.
       */
      _nudgeEngravingRender() {
        if (!this.viewer) return;
        const tick = () => this.viewer && this.viewer.setDirty();
        tick();
        requestAnimationFrame(() => requestAnimationFrame(tick));
        if (this._engravingRenderTimers) {
          this._engravingRenderTimers.forEach((t) => clearTimeout(t));
        }
        this._engravingRenderTimers = [
          setTimeout(tick, 120),
          setTimeout(tick, 360),
        ];
      }

      async applyMetal(metalType, meshes = this.meshes) {
        if (!metalType) return;
        const urls = MATERIAL_URLS[metalType];
        if (!urls) return;

        // Save hallmark textures before .pmat overwrites them
        let hallmarkNormalMap = null;
        let hallmarkAoMap = null;
        let hallmarkNormalScale = null;
        if (meshes.hallmark && meshes.hallmark.material) {
          hallmarkNormalMap = meshes.hallmark.material.normalMap;
          hallmarkAoMap = meshes.hallmark.material.aoMap;
          if (meshes.hallmark.material.normalScale) {
            hallmarkNormalScale = meshes.hallmark.material.normalScale.clone();
          }
        }

        const meshMaterialPairs = [
          [meshes.ringBody, urls.body],
          [meshes.hallmark, urls.hallmark],
          [meshes.insideEngrave, urls.insideEngrave],
          [meshes.outsideEngraveLeft, urls.outsideEngraveLeft],
          [meshes.outsideEngraveRight, urls.outsideEngraveRight],
          // Plain band GLBs have a single Outside_Engrave_Full mesh instead
          // of Left/Right split. Without giving it the right .pmat the mesh
          // keeps whatever the GLB shipped (typically a different metal
          // entirely or an uninitialised material), and SimpleTextPlugin's
          // bumpMap/map writes never become visible against the ring.
          [meshes.outsideEngraveFull, urls.outsideEngraveLeft],
        ].filter(([mesh]) => mesh);

        try {
          await Promise.all(meshMaterialPairs.map(async ([mesh, url]) => {
            const material = await this.manager.importer.importSinglePath(url);
            if (material) {
              if (mesh.setMaterial) {
                mesh.setMaterial(material);
              } else {
                mesh.material = material;
              }
              mesh.material.needsUpdate = true;
              if (mesh.material.setDirty) mesh.material.setDirty();
            }
          }));
        } catch (e) {
          console.warn('[3D Ring Viewer] .pmat loading failed, using fallback presets:', e.message);
          this.applyMetalFallback(metalType, meshes);
        }

        // Restore hallmark textures after .pmat swap
        if (meshes.hallmark && meshes.hallmark.material) {
          const mat = meshes.hallmark.material;
          if (hallmarkNormalMap) {
            mat.normalMap = hallmarkNormalMap;
            mat.normalScale = hallmarkNormalScale || null;
          }
          if (hallmarkAoMap) mat.aoMap = hallmarkAoMap;
          mat.userData.ssaoDisabled = true;
          mat.needsUpdate = true;
          if (mat.setDirty) mat.setDirty();
        }

        // Re-apply engraving — `applyMetal` overwrites the engrave meshes'
        // materials, which wipes SimpleTextPlugin's bumpMap/map writes.
        // Without this the customer sees a blank ring after every metal
        // swap (Band Only ⇄ With Diamond, metal carat changes). Schedule
        // across frames so the new material's texture slots are ready
        // before the plugin tries to paint into them.
        this._scheduleFlushPendingEngraving();

        this.viewer.setDirty();
      }

      applyMetalFallback(metalType, meshes = this.meshes) {
        const preset = METAL_PRESETS_FALLBACK[metalType];
        if (!preset) return;

        const metalMeshes = [
          meshes.ringBody,
          meshes.insideEngrave,
          meshes.outsideEngraveLeft,
          meshes.outsideEngraveRight,
          meshes.outsideEngraveFull,
          meshes.hallmark,
        ].filter(Boolean);

        for (const mesh of metalMeshes) {
          const mat = mesh.material;
          if (!mat) continue;
          mat.color.set(preset.color);
          mat.metalness = preset.metalness;
          mat.roughness = preset.roughness;
          mat.envMapIntensity = preset.envMapIntensity || 1.5;
          mat.needsUpdate = true;
          if (mat.setDirty) mat.setDirty();
        }
        // Same engraving-restore reason as applyMetal: the colour write
        // above touches the engrave mesh materials too.
        this._scheduleFlushPendingEngraving();
        this.viewer.setDirty();
      }

      applyDiamondMaterial(meshes = this.meshes) {
        const diamond = meshes.diamond;
        if (!diamond || !diamond.material) return;

        const dp = this.viewer.getPlugin('Diamond');
        if (!dp) return;

        // RSFY-680 — the public `makeDiamondMesh` API is the supported way to
        // convert the mesh's material into a Diamond material in one step.
        // The previous path called `prepareDiamondMesh` + the private
        // `_convertToDiamondMaterial` helper, which sometimes returned a
        // material that was *not* flagged as a diamond (userData.isDiamond
        // remained false), so the mesh kept rendering as plain gray.
        if (typeof dp.makeDiamondMesh === 'function') {
          try {
            dp.makeDiamondMesh(diamond, {
              cacheKey: diamond.geometry.uuid,
              normalMapRes: 512,
            });
            this.viewer.setDirty();
            return;
          } catch (e) {
            console.warn('[3D Ring Viewer] makeDiamondMesh failed, falling back to legacy path:', e.message);
          }
        }

        // Fallback for older bundles that don't expose makeDiamondMesh.
        dp.prepareDiamondMesh(diamond, {
          cacheKey: diamond.geometry.uuid,
          normalMapRes: 512,
        });
        const origMat = diamond.material;
        const diamondMat = dp._convertToDiamondMaterial(origMat, { isDiamond: true }, false);
        if (!diamondMat) return;
        if (diamond.setMaterial) {
          diamond.setMaterial(diamondMat);
        } else {
          diamond.material = diamondMat;
        }
        diamondMat.needsUpdate = true;
        if (diamondMat.setDirty) diamondMat.setDirty();
        this.viewer.setDirty();
      }

      applyPolygonOffset(meshes = this.meshes) {
        const isApple = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
        const factor = isApple ? -1 : -1.75;
        const units = isApple ? -2 : 0;

        const engraveMeshes = [
          meshes.hallmark,
          meshes.insideEngrave,
          meshes.outsideEngraveLeft,
          meshes.outsideEngraveRight,
          // Plain band GLBs use a single Outside_Engrave_Full mesh in place
          // of the diamond ring's Left/Right split. Without polygon offset
          // it z-fights with Ring_Body and the text disappears under the
          // band surface — exactly the symptom on the plain band path.
          meshes.outsideEngraveFull,
        ].filter(Boolean);

        for (const mesh of engraveMeshes) {
          const mat = mesh.material;
          if (!mat) continue;
          mat.polygonOffset = true;
          mat.polygonOffsetFactor = factor;
          mat.polygonOffsetUnits = units;
          mat.needsUpdate = true;
          if (mat.setDirty) mat.setDirty();
        }
        this.viewer.setDirty();
      }

      // =========================================================================
      // Engraving — uses WebGI SimpleTextPlugin (SVG-based, same as original viewer)
      // =========================================================================

      updateEngraving(side, text, font) {
        // Run the actual texture write, then nudge the renderer so the
        // freshly-uploaded engraving texture paints even when no other
        // interaction follows (e.g. a lone Elvish→English font switch).
        // _applyEngraving has several early returns, so the wrapper guarantees
        // the nudge fires on every path that touched the plugin.
        this._applyEngraving(side, text, font);
        this._nudgeEngravingRender();
      }

      /**
       * Break any URL in the engraving text so SimpleTextPlugin's embedUrlRefs
       * (which fetches every `(ftp|https?)://…` it finds in the generated SVG)
       * doesn't try to download the inscription and crash the texture build on
       * CORS. A zero-width space inside "://" defeats the plugin's URL regex
       * — /(((ftp|https?):\/\/)…)/ — yet renders invisibly, so the engraving
       * still reads as the customer typed it. Render-only; the real text is
       * stashed separately for the cart value.
       */
      _sanitizeEngravingText(text) {
        if (!text || typeof text !== 'string') return text;
        const ZWSP = '​';
        return text.replace(/(ftp|https?):\/\//gi, (m) => m.replace('//', '/' + ZWSP + '/'));
      }

      _applyEngraving(side, text, font) {
        // Always stash the latest write — the model may swap (e.g. plain
        // band ⇄ diamond ring) at any point, and we want the next load to
        // be able to replay the customer's chosen engraving without them
        // having to re-edit. Defensively initialize the stash if a
        // previous build of this element ran without it.
        if (!this._pendingEngraving) this._pendingEngraving = { outside: null, inside: null };
        this._pendingEngraving[side === 'inside' ? 'inside' : 'outside'] = { text, font };
        if (!this.textPlugin) return;

        // RSFY — Neutralise any URL inside the engraving text before it reaches
        // the text plugin. SimpleTextPlugin.makeTextSvg embeds the inscription
        // into an SVG, then runs embedUrlRefs() over the whole SVG to inline
        // every `(ftp|https?)://…` reference as a data URI (this is how it
        // bundles the @font-face files). When the customer engraves a literal
        // URL (e.g. "https://www.jenshansen.com/prod"), that regex matches the
        // INSCRIPTION too, the plugin tries to fetch it, it dies on CORS, and
        // importSinglePath throws — so the texture is never built and the
        // engraving renders completely blank. Elvish escaped this only because
        // the text is transliterated to Tengwar first, dropping the "://".
        // Inserting a zero-width space inside "://" breaks the URL regex while
        // leaving the glyphs visually identical. The real, untouched text is
        // already stashed above for the cart/order value.
        text = this._sanitizeEngravingText(text);

        const isInside = side === 'inside';
        // Engraving font size mirrors the original PDP ring viewer, which
        // passes raw fontSizes (51 inside, 55/120 outside) with NO per-font
        // EM-square correction. The inside/outside blocks below each derive
        // their own size from those bases × an optional per-product metafield
        // scale (data-*-font-scale), so `scaleForFont` is intentionally not
        // applied to engraving here.
        const yOffset = parseInt(
          isInside ? this.dataset.insideYOffset : this.dataset.outsideYOffset
        ) || 0;
        const xOffset = isInside ? 0 : (parseInt(this.dataset.outsideXOffset) || 0);

        if (isInside) {
          const mesh = this.meshes.insideEngrave;
          if (!mesh) return;

          if (!text || text.trim() === '') {
            this.textPlugin.updateText(mesh, { text: '', style: this._fontStyles });
            return;
          }

          // Inside engraving — replicate the original PDP ring viewer 1:1.
          // The PDP computes inside fontSize = 51 × (per-product inside
          // multiplier). That multiplier is 0.55 on plain band-only products
          // (→ 28.05) but 1.0 on engagement/diamond products (→ 51). Our
          // catalogue ships those multipliers as BLANK metafields, so we pick
          // the base by ring type instead: a diamond ring has the split
          // outside meshes (Left/Right) and no single Outside_Engrave_Full.
          // The PDP applies no per-font EM scale, so we pass the raw fontSize
          // (no scaleForFont) and match every box param. A per-product
          // metafield (data-inside-font-scale) still overrides as a multiplier
          // on the 51 base when the merchant has set one.
          const isDiamondRing = !this.meshes.outsideEngraveFull &&
            !!(this.meshes.outsideEngraveLeft || this.meshes.outsideEngraveRight);
          const explicitInsideScale = parseFloat(this.dataset.insideFontScale);
          const insideMultiplier = Number.isFinite(explicitInsideScale) && explicitInsideScale > 0
            ? explicitInsideScale
            : (isDiamondRing ? 1.0 : 0.55);
          const insideFontSize = 51 * insideMultiplier; // band-only 28.05 / diamond 51
          this.textPlugin.updateText(mesh, {
            text: text,
            fontFamily: font || 'Kereru',
            style: this._fontStyles,
            fontSize: insideFontSize,
            textColor: '#000000',
            width: 4096,
            height: 4096,
            textAnchor: 'start',
            xOffset: -475,
            boxWidth: 1024,
            boxHeight: 1024,
            yOffset: yOffset,
          });
        } else {
          // Plain band GLBs ship a single Outside_Engrave_Full mesh that
          // covers the whole outside surface (no centre stone to dodge), so
          // prefer that when present and skip the Left/Right split.
          const meshFull = this.meshes.outsideEngraveFull;
          const meshLeft = this.meshes.outsideEngraveLeft;
          const meshRight = this.meshes.outsideEngraveRight;
          if (!meshFull && !meshLeft && !meshRight) return;

          // Outside engraving font size — mirror the original PDP 1:1
          // (raw fontSize, no per-font EM scale) in both ring types:
          //  • BAND-ONLY (single Outside_Engrave_Full mesh) → 55.
          //  • DIAMOND (Left/Right split meshes) → 120 (defaultOutsideFontSize).
          // A per-product metafield scale (data-outside-font-scale) still
          // applies in both cases when the merchant has set one.
          const explicitOutsideScale = parseFloat(this.dataset.outsideFontScale);
          const outsideSizeScale = Number.isFinite(explicitOutsideScale) && explicitOutsideScale > 0
            ? explicitOutsideScale
            : 1.0;
          const fontSizeFull = 55 * outsideSizeScale;          // band-only meshFull
          // Diamond left/right — mirror the original PDP `defaultOutsideFontSize`
          // of 120 (× per-product metafield scale). The PDP applies NO per-font
          // EM scale here either, so we pass raw 120, not 120 × scaleForFont.
          const fontSize = 120 * outsideSizeScale;              // diamond left/right

          if (!text || text.trim() === '') {
            if (meshFull) this.textPlugin.updateText(meshFull, { text: '', style: this._fontStyles });
            if (meshLeft) this.textPlugin.updateText(meshLeft, { text: '', style: this._fontStyles });
            if (meshRight) this.textPlugin.updateText(meshRight, { text: '', style: this._fontStyles });
            return;
          }

          if (meshFull) {
            // Plain band single-mesh outside engraving — mirror the original
            // PDP `outsideRawTextFullandLeft` object exactly: canvas
            // 4096×2048, boxWidth 2048 / boxHeight 1024, textAnchor 'start',
            // xOffset -1000 (+ any per-product metafield xOffset). This is the
            // 'Outside_Engrave_Full' equivalent the PDP renders for band-only
            // rings, so the engraving sits and sizes identically.
            this.textPlugin.updateText(meshFull, {
              text: text,
              fontFamily: font || 'Elvish',
              style: this._fontStyles,
              fontSize: fontSizeFull,
              textColor: '#000000',
              width: 4096,
              height: 2048,
              textAnchor: 'start',
              xOffset: -1000 + xOffset,
              boxWidth: 2048,
              boxHeight: 1024,
              yOffset: yOffset,
            });
            return;
          }

          // Diamond ring path — split text at nearest space to middle so
          // each face holds half of the inscription (matching original viewer)
          const middle = Math.floor(text.length / 2);
          let splitPoint = text.lastIndexOf(' ', middle);
          const afterSpace = text.indexOf(' ', middle + 1);
          if (splitPoint === -1 || (afterSpace !== -1 && middle - splitPoint >= afterSpace - middle)) {
            splitPoint = afterSpace;
          }

          if (splitPoint === -1) {
            // No space to split on (a single word — a name, or a one-word
            // Tengwar transcription). The original PDP viewer resolves this
            // as `substr(0, -1)` → '' for the left face and the whole word on
            // the RIGHT face, anchored 'start' just past the stone. Mirror
            // that exactly: prod-vs-dev pixel diff (2026-09-02) caught the
            // port putting the word on the LEFT face instead, so every
            // single-name engraving rendered on the opposite side of the
            // stone from the live site. Fall back to the left face only when
            // the model has no right face at all.
            //
            // Always WIPE the other face: engrave "One ring to show our love,
            // One ring to bind us" (splits across both faces), then replace it
            // with a single word and the stale half would otherwise remain.
            const target = meshRight || meshLeft;
            const other = meshRight ? meshLeft : null;
            if (other) {
              this.textPlugin.updateText(other, { text: '', style: this._fontStyles });
            }
            if (target) {
              this.textPlugin.updateText(target, {
                text: text,
                fontFamily: font || 'Elvish',
                style: this._fontStyles,
                fontSize: fontSize,
                textColor: '#000000',
                width: 4096,
                height: 2048,
                textAnchor: meshRight ? 'start' : 'end',
                xOffset: meshRight ? -(975 + xOffset) : 975 + xOffset,
                boxWidth: 2048,
                boxHeight: 1024,
                yOffset: yOffset,
              });
            }
          } else {
            const leftText = text.substring(0, splitPoint);
            const rightText = text.substring(splitPoint + 1);

            if (meshLeft) {
              this.textPlugin.updateText(meshLeft, {
                text: leftText,
                fontFamily: font || 'Elvish',
                style: this._fontStyles,
                fontSize: fontSize,
                textColor: '#000000',
                width: 4096,
                height: 2048,
                textAnchor: 'end',
                xOffset: 975 + xOffset,
                boxWidth: 2048,
                boxHeight: 1024,
                yOffset: yOffset,
              });
            }
            if (meshRight) {
              this.textPlugin.updateText(meshRight, {
                text: rightText,
                fontFamily: font || 'Elvish',
                style: this._fontStyles,
                fontSize: fontSize,
                textColor: '#000000',
                width: 4096,
                height: 2048,
                textAnchor: 'start',
                xOffset: -(975 + xOffset),
                boxWidth: 2048,
                boxHeight: 1024,
                yOffset: yOffset,
              });
            }
          }
        }
      }

      // =========================================================================
      // UI — disclaimer, loading, error
      // =========================================================================

      showDisclaimer() {
        const disclaimerEl = this.querySelector('.ring-viewer-3d__disclaimer');
        const hintEl = this.querySelector('.ring-viewer-3d__interaction-hint');
        const disclaimer = this.dataset.variantADisclaimer;
        const sizing = this.dataset.picturedSizing;
        const metalType = this.dataset.metalType || '';

        if (disclaimerEl && (disclaimer || sizing)) {
          let text = "We'll optimize your final design! *Showing ";
          if (disclaimer) text += disclaimer;
          if (metalType) text += ` ${metalType}`;
          if (sizing) text += ` in size ${sizing}`;
          disclaimerEl.innerHTML = text;
          disclaimerEl.style.display = '';
        }

        if (hintEl) {
          const isMobile = window.matchMedia('(max-width: 767px)').matches;
          hintEl.textContent = isMobile
            ? 'Swipe to Rotate & Pinch to Zoom/Move'
            : 'Drag to Rotate, Scroll to Zoom, Right-Click to Move';
        }
      }

      updateDisclaimer(disclaimer) {
        const disclaimerEl = this.querySelector('.ring-viewer-3d__disclaimer');
        if (disclaimer && disclaimerEl) {
          const metalType = this.dataset.metalType || '';
          const sizing = this.dataset.picturedSizing || '';
          let text = "We'll optimize your final design! *Showing ";
          text += disclaimer;
          if (metalType) text += ` ${metalType}`;
          if (sizing) text += ` in size ${sizing}`;
          disclaimerEl.innerHTML = text;
          disclaimerEl.style.display = '';
        } else if (disclaimerEl) {
          disclaimerEl.style.display = 'none';
        }
      }

      // =========================================================================
      // Events — engraving form, variant change
      // =========================================================================

      bindEvents() {
        this.boundEngravingHandler = this.handleEngravingUpdate.bind(this);
        document.addEventListener('ring-engraving-update', this.boundEngravingHandler);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.handleVariantChange(event);
          });
        }

        this.applyCurrentEngravingState();
      }

      applyCurrentEngravingState() {
        const form = document.querySelector('engraving-form');
        if (!form) return;
        ['outside', 'inside'].forEach((side) => {
          const textarea = form.querySelector(`.engraving-form__textarea[data-side="${side}"]`);
          const select = form.querySelector(`.engraving-form__select[data-side="${side}"]`);
          if (textarea && !textarea.disabled && textarea.value.trim()) {
            const font = select ? select.value : '';
            this.handleEngravingUpdate({ detail: { side, text: textarea.value, font } });
          }
        });
      }

      handleEngravingUpdate(event) {
        const { side, text, displayText, font } = event.detail;
        // engraving-form.js sends displayText already transliterated (and
        // error-markup-stripped) for Elvish — use it verbatim. Transcribe's
        // Tengwar output is ASCII that the Annatar font maps to glyphs, so
        // no "is this Latin?" test can tell it apart from raw input; running
        // transcribe over displayText mangles it, and its <abbr> error markup
        // then breaks SimpleTextPlugin's SVG texture build (blank engraving).
        // Transliterate here ONLY for callers that pass raw text with no
        // displayText (applyCurrentEngravingState after a model swap).
        let renderText;
        if (displayText != null) {
          renderText = displayText;
        } else {
          renderText = text;
          if (font === 'Elvish' && renderText && typeof window.transcribe === 'function') {
            // Match the canonical translator's options so the 3D engraving
            // and the website preview agree on the tehtar placement (Halfdan's
            // parity fix — see renderElvishPreview in risify-ring-builder.js).
            try {
              renderText = this._stripTranscribeErrorMarkup(
                window.transcribe(renderText, {
                  noAchLaut: true,
                  medialOre: true,
                  language: 'english',
                })
              );
            } catch (e) {}
          }
        }
        this.updateEngraving(side, renderText, font);
      }

      // transcribe() flags untranscribable characters as
      // `<abbr class="error" title="…">.</abbr>`. Keep the fallback glyph,
      // drop the markup — raw tags would be embedded into SimpleTextPlugin's
      // SVG texture and kill the build. Mirrors _stripElvishErrorMarkup in
      // engraving-form.js.
      _stripTranscribeErrorMarkup(str) {
        if (!str || typeof str !== 'string') return str || '';
        let cleaned = str.replace(/<abbr\b[^>]*>([\s\S]*?)<\/abbr>/gi, '$1');
        cleaned = cleaned.replace(/<\/?[a-z][^>]*>/gi, '');
        return cleaned
          .replace(/&quot;/g, '"')
          .replace(/&#34;/g, '"')
          .replace(/&apos;/g, "'")
          .replace(/&#39;/g, "'")
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&');
      }

      async handleVariantChange(event) {
        const variant = event.data?.variant;
        const termB = this.dataset.variantBTerm;
        if (!termB || !variant || !this.dataset.variantBModel) return;
        const matchesB = variant.options?.some(
          (opt) => opt.toLowerCase().includes(termB.toLowerCase())
        );
        const target = matchesB ? this.dataset.variantBModel : this.dataset.variantAModel;
        await this.loadModel(target);
        this.updateDisclaimer(matchesB ? this.dataset.variantBDisclaimer : this.dataset.variantADisclaimer);
        // Materials are prepared once per resident set and shared between
        // sets, so a switch is a visibility flip with no engraving rebuild.
        await this._prepareModelSet(this._modelSets[target]);
      }

      hideLoading() {
        const loading = this.querySelector('.ring-viewer-3d__loading');
        if (loading) loading.classList.add('hidden');
      }

      /**
       * Bug fix #12 — Runtime fallback for the WebGI loading-screen overlay.
       * Watches every subtree mutation under this custom element. Any node
       * that *looks* like a loading/progress widget (other than our own
       * `.ring-viewer-3d__loading`) is hidden immediately. This catches the
       * overlay no matter what class name the bundle decides to use.
       */
      _installLoadingOverlaySuppressor() {
        if (this._loadingObserver) return;
        const isOurSpinner = (el) => el && (
          el.classList?.contains('ring-viewer-3d__loading') ||
          el.classList?.contains('ring-viewer-3d__spinner') ||
          el.classList?.contains('ring-viewer-3d__loading-text')
        );
        const looksLikeWebGiOverlay = (el) => {
          if (!el || el.nodeType !== 1 || isOurSpinner(el)) return false;
          // Match by class hint…
          const cls = (el.className && el.className.baseVal) || el.className || '';
          if (typeof cls === 'string' && /loading|progress|loader|webgi|threepipe|pixotronics/i.test(cls)) return true;
          // …or by textContent if the overlay just renders "99%" / "100%".
          const txt = (el.textContent || '').trim();
          if (txt && /^\s*\d{1,3}\s*%\s*$/.test(txt) && el.children.length === 0) return true;
          return false;
        };
        const hide = (el) => {
          try {
            el.style.setProperty('display', 'none', 'important');
            el.style.setProperty('visibility', 'hidden', 'important');
            el.style.setProperty('pointer-events', 'none', 'important');
          } catch (_) {}
        };
        // Sweep what's already in the tree.
        this.querySelectorAll('*').forEach((el) => {
          if (looksLikeWebGiOverlay(el)) hide(el);
        });
        // Watch for future insertions.
        this._loadingObserver = new MutationObserver((mutations) => {
          mutations.forEach((m) => {
            m.addedNodes.forEach((node) => {
              if (node.nodeType !== 1) return;
              if (looksLikeWebGiOverlay(node)) hide(node);
              // Also sweep descendants of the new subtree.
              if (typeof node.querySelectorAll === 'function') {
                node.querySelectorAll('*').forEach((child) => {
                  if (looksLikeWebGiOverlay(child)) hide(child);
                });
              }
            });
            // Class attribute changes can also reveal an overlay.
            if (m.type === 'attributes' && m.target && looksLikeWebGiOverlay(m.target)) {
              hide(m.target);
            }
          });
        });
        this._loadingObserver.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      showError() {
        const spinner = this.querySelector('.ring-viewer-3d__spinner');
        const text = this.querySelector('.ring-viewer-3d__loading-text');
        if (spinner) spinner.style.display = 'none';
        if (text) text.textContent = 'Unable to load 3D model';
      }

      // =========================================================================
      // Public API — called by Ring Builder (risify-ring-builder.js)
      // =========================================================================

      async initManual() {
        if (this.initialized) return;
        this.initialized = true;
        this.init();
      }

      async loadModelFromUrl(url) {
        if (!url) return;
        if (!this.initialized) await this.initManual();
        await this._waitUntilReady();
        if (this.currentModelUrl === url && Object.keys(this.meshes).length > 0) return;
        await this.loadModel(url);
      }

      async setMetal(metalType) {
        if (!metalType || !this.viewer) return;
        await this.applyMetal(metalType);
        this.applyPolygonOffset();
      }

      async setDiamond(visible = true) {
        if (!this.viewer) return;
        // RSFY-676 — when the customer hasn't added a diamond (Movie Ring /
        // plain band path), hide the diamond mesh entirely instead of just
        // skipping material application. The current catalogue ships only
        // diamond-bearing GLB models, so this is how we surface "no stone"
        // imagery without an extra plain-band GLB asset.
        const diamond = this.meshes?.diamond;
        if (diamond) {
          diamond.visible = visible;
          if (diamond.material?.setDirty) diamond.material.setDirty();
        }
        if (visible) {
          this.applyDiamondMaterial();
        } else {
          this.viewer.setDirty();
        }
      }

      // Show/hide the inside hallmark mesh. Same approach as setDiamond — the
      // mesh is part of every GLB, we just toggle its visibility from the
      // builder's Hallmark Yes/No control (Bug B fix).
      setHallmark(visible = true) {
        if (!this.viewer) return;
        const hallmark = this.meshes?.hallmark;
        if (!hallmark) return;
        hallmark.visible = !!visible;
        if (hallmark.material?.setDirty) hallmark.material.setDirty();
        this.viewer.setDirty();
      }

      updateEngravingFromBuilder(side, text, font) {
        if (!this.viewer) return;
        // Defensive: if the caller passed Latin chars with Elvish font and
        // window.transcribe is available, transliterate on the way in.
        // Options mirror the canonical translator (noAchLaut/medialOre/english)
        // so the 3D engraving agrees with the website translator preview.
        let renderText = text;
        if (font === 'Elvish' && renderText && /[A-Za-z]/.test(renderText) && typeof window.transcribe === 'function') {
          try {
            renderText = window.transcribe(renderText, {
              noAchLaut: true,
              medialOre: true,
              language: 'english',
            });
          } catch (e) {}
        }
        this.updateEngraving(side, renderText, font);
      }

      _waitUntilReady() {
        return new Promise((resolve) => {
          if (this.viewer) { resolve(); return; }
          const check = setInterval(() => {
            if (this.viewer) { clearInterval(check); resolve(); }
          }, 100);
          setTimeout(() => { clearInterval(check); resolve(); }, 15000);
        });
      }
    }
  );
}
